import React from "react";
import AgentSelect from "../common/AgentSelect";
import { packageNetPrice } from "../../lib/catalog";

export const EMPTY_PACKAGE_FILTERS = {
  category: "",
  status: "",
  priceMin: "",
  priceMax: "",
  duration: "",
  dateFrom: "",
  dateTo: "",
};

export const DURATION_OPTIONS = [
  { value: "1-10", label: "Up to 10 days" },
  { value: "11-14", label: "11–14 days" },
  { value: "15-21", label: "15–21 days" },
  { value: "22+", label: "22+ days" },
];

export const PACKAGE_CATEGORY_OPTIONS = [
  { value: "Umrah", label: "Umrah" },
  { value: "Hajj", label: "Hajj" },
  { value: "Ziyarath", label: "Ziyarath" },
];

export const PACKAGE_STATUS_OPTIONS = [
  { value: "Available", label: "Available" },
  { value: "Not Available", label: "Not Available" },
];

export function emptyPackageFilters() {
  return { ...EMPTY_PACKAGE_FILTERS };
}

export function countPackageFilters(filters = EMPTY_PACKAGE_FILTERS) {
  let count = 0;
  if (filters.category) count += 1;
  if (filters.status) count += 1;
  if (filters.priceMin !== "" || filters.priceMax !== "") count += 1;
  if (filters.duration) count += 1;
  if (filters.dateFrom || filters.dateTo) count += 1;
  return count;
}

function parseDurationDays(value) {
  const match = String(value ?? "").match(/(\d+)/);
  return match ? Number(match[1]) : null;
}

function toDateKey(value) {
  if (!value) return "";
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const iso = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function durationMatches(days, durationKey) {
  if (!durationKey) return true;
  if (days == null || Number.isNaN(days)) return false;
  if (durationKey === "22+") return days >= 22;
  const [min, max] = String(durationKey).split("-").map(Number);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return String(days) === String(durationKey);
  return days >= min && days <= max;
}

function categoryMatches(pkgType, category) {
  if (!category) return true;
  const type = String(pkgType || "").toLowerCase();
  const selected = String(category).toLowerCase();
  if (!type) return false;
  if (selected === "umrah") return type.includes("umrah");
  if (selected === "hajj") return type.includes("hajj");
  if (selected === "ziyarath") return /ziyarath|ziyarat|ziyarah/.test(type);
  return type === selected;
}

export function packageMatchesFilters(pkg, filters = EMPTY_PACKAGE_FILTERS) {
  if (!categoryMatches(pkg.type, filters.category)) {
    return false;
  }

  if (filters.status) {
    const selected = String(filters.status).toLowerCase();
    const status = String(pkg.status || "").toLowerCase();
    const isAvailable = status === "available";
    if (selected === "available" && !isAvailable) return false;
    if (selected === "not available" && isAvailable) return false;
  }

  const price = Number(packageNetPrice(pkg)) || 0;
  if (filters.priceMin !== "" && price < Number(filters.priceMin)) return false;
  if (filters.priceMax !== "" && price > Number(filters.priceMax)) return false;

  if (filters.duration && !durationMatches(parseDurationDays(pkg.duration), filters.duration)) {
    return false;
  }

  const departure = toDateKey(pkg.departureDate);
  if (filters.dateFrom && departure && departure < filters.dateFrom) return false;
  if (filters.dateTo && departure && departure > filters.dateTo) return false;
  if ((filters.dateFrom || filters.dateTo) && !departure) return false;

  return true;
}

export function packageFilterOptions() {
  return {
    categoryOptions: PACKAGE_CATEGORY_OPTIONS,
    statusOptions: PACKAGE_STATUS_OPTIONS,
  };
}

function FilterField({ label, htmlFor, className = "", children }) {
  return (
    <div className={`package-filter-bar__field ${className}`.trim()}>
      <label className="package-filter-bar__label" htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

export default function PackageFilterBar({
  search,
  onSearchChange,
  onSearchSubmit,
  filters,
  onFilterChange,
}) {
  function setField(name, value) {
    onFilterChange({ ...filters, [name]: value });
  }

  return (
    <div className="package-filter-bar" role="search">
      <FilterField label="Search" htmlFor="package-filter-search" className="package-filter-bar__search">
        <input
          id="package-filter-search"
          type="search"
          className="form-control"
          placeholder="Search packages"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onBlur={onSearchSubmit}
          onKeyDown={(event) => {
            if (event.key === "Enter") onSearchSubmit();
          }}
        />
      </FilterField>

      <FilterField label="Category" htmlFor="package-filter-category">
        <AgentSelect
          inputId="package-filter-category"
          value={filters.category}
          options={PACKAGE_CATEGORY_OPTIONS}
          onChange={(value) => setField("category", value)}
          placeholder="Category"
          isClearable
          isSearchable={false}
        />
      </FilterField>

      <FilterField label="Status" htmlFor="package-filter-status">
        <AgentSelect
          inputId="package-filter-status"
          value={filters.status}
          options={PACKAGE_STATUS_OPTIONS}
          onChange={(value) => setField("status", value)}
          placeholder="Status"
          isClearable
          isSearchable={false}
        />
      </FilterField>

      <FilterField label="Price Range" htmlFor="package-filter-price-min" className="package-filter-bar__price">
        <div className="package-filter-range">
          <input
            id="package-filter-price-min"
            type="number"
            min="0"
            inputMode="numeric"
            className="form-control"
            placeholder="Min"
            value={filters.priceMin}
            onChange={(event) => setField("priceMin", event.target.value)}
          />
          <span className="package-filter-range-sep">-</span>
          <input
            id="package-filter-price-max"
            type="number"
            min="0"
            inputMode="numeric"
            className="form-control"
            placeholder="Max"
            value={filters.priceMax}
            onChange={(event) => setField("priceMax", event.target.value)}
          />
        </div>
      </FilterField>

      <FilterField label="Duration" htmlFor="package-filter-duration">
        <AgentSelect
          inputId="package-filter-duration"
          value={filters.duration}
          options={DURATION_OPTIONS}
          onChange={(value) => setField("duration", value)}
          placeholder="Duration"
          isClearable
          isSearchable={false}
        />
      </FilterField>
    </div>
  );
}
